import { useState, useMemo } from 'react';
import { portalColors, portalRadius } from './portalStyles';
import { JobHeroCard } from './JobHeroCard';
import { JobCompactCard } from './JobCompactCard';
import type { CrewPortalJob } from '../../types/crewPortal';

interface Props {
  todayJobs: CrewPortalJob[];
  upcomingJobs: CrewPortalJob[];
  historyJobs: { id: string; title: string; scheduled_date: string; status: string; location_name: string | null }[];
  accentColor: string;
  firstName: string;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

function getWeekDays(): { date: Date; dateStr: string; dayName: string; dayNum: number }[] {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return { date: d, dateStr: toDateStr(d), dayName: DAY_NAMES[i], dayNum: d.getDate() };
  });
}

export function WeekView({ todayJobs, upcomingJobs, historyJobs, accentColor, firstName }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const todayStr = toDateStr(new Date());
  const weekDays = useMemo(getWeekDays, []);

  // Build date → jobs map from all sources
  const jobsByDate = useMemo(() => {
    const map = new Map<string, CrewPortalJob[]>();
    for (const j of todayJobs) {
      const arr = map.get(j.scheduled_date) ?? [];
      arr.push(j);
      map.set(j.scheduled_date, arr);
    }
    for (const j of upcomingJobs) {
      const arr = map.get(j.scheduled_date) ?? [];
      arr.push(j);
      map.set(j.scheduled_date, arr);
    }
    return map;
  }, [todayJobs, upcomingJobs]);

  const historyDates = useMemo(() => new Set(historyJobs.map(h => h.scheduled_date)), [historyJobs]);

  // All week jobs in order
  const weekJobEntries = useMemo(() => {
    const entries: { dateStr: string; label: string; job: CrewPortalJob; isPast: boolean; isToday: boolean }[] = [];
    for (const day of weekDays) {
      const jobs = jobsByDate.get(day.dateStr) ?? [];
      for (const job of jobs) {
        entries.push({
          dateStr: day.dateStr,
          label: formatDateLabel(day.dateStr),
          job,
          isPast: day.dateStr < todayStr,
          isToday: day.dateStr === todayStr,
        });
      }
    }
    return entries;
  }, [weekDays, jobsByDate, todayStr]);

  const totalJobs = weekJobEntries.length;
  const locationSet = new Set(weekJobEntries.map(e => e.job.location?.name).filter(Boolean));

  return (
    <div>
      {/* Week strip */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {weekDays.map(day => {
          const isToday = day.dateStr === todayStr;
          const hasJobs = jobsByDate.has(day.dateStr) || historyDates.has(day.dateStr);
          return (
            <div key={day.dateStr} style={{
              flex: 1, textAlign: 'center', borderRadius: portalRadius.calDay,
              padding: '8px 4px', cursor: 'default',
              background: isToday ? accentColor : hasJobs ? `${accentColor}1A` : portalColors.divider,
            }}>
              <div style={{ fontSize: 11, color: isToday ? '#fff' : portalColors.muted }}>{day.dayName}</div>
              <div style={{
                fontSize: 14, fontWeight: isToday ? 600 : 400,
                color: isToday ? '#fff' : hasJobs ? accentColor : portalColors.secondary,
              }}>{day.dayNum}</div>
              {hasJobs && (
                <div style={{
                  width: 5, height: 5, borderRadius: '50%', margin: '3px auto 0',
                  background: isToday ? '#fff' : accentColor,
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {totalJobs > 0 && (
        <div style={{ fontSize: 13, fontWeight: 500, color: accentColor, marginBottom: 16 }}>
          {totalJobs} job{totalJobs !== 1 ? 's' : ''} this week{locationSet.size > 0 ? ` \u00B7 ${locationSet.size} ${locationSet.size === 1 ? 'location' : 'locations'}` : ''}
        </div>
      )}

      {/* Job cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {weekJobEntries.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', fontSize: 14, color: portalColors.secondary }}>
            No jobs scheduled this week. Enjoy the downtime.
          </div>
        )}
        {weekJobEntries.map(entry => (
          entry.isToday ? (
            <JobHeroCard key={entry.job.id} job={entry.job} accentColor={accentColor} label="Today" firstName={firstName} />
          ) : (
            <JobCompactCard
              key={entry.job.id}
              job={entry.job}
              accentColor={accentColor}
              dateLabel={entry.label}
              isPast={entry.isPast}
              isExpanded={expandedId === entry.job.id}
              onExpand={() => setExpandedId(expandedId === entry.job.id ? null : entry.job.id)}
              firstName={firstName}
            />
          )
        ))}
      </div>
    </div>
  );
}
